/**
 * Utility functions for generating consistent new cart item IDs
 * Based on patterns from generate-web-cart-item.ts
 */

import type { ProductModel } from '@kidslabs/module-catalog';

export class NewCartItemIdGenerator {
    /**
     * Generate a new cart item ID based on item ID (simple products)
     * Format: new-{itemId}
     * Used in generate-web-cart-item.ts line 23
     */
    static generateFromItemId(itemId: number | string): string {
        return `new-${itemId}`;
    }

    /**
     * Generate a new cart item ID for configurable product
     * Format: new-{itemId}.{productId}
     * Used in generate-web-cart-item.ts line 80
     */
    static generateForConfigurable(itemId: string, productId: number | string): string {
        return `new-${itemId}.${productId}`;
    }

    /**
     * Generate a temporary ID with timestamp and random suffix (for stale references)
     * Format: new-{timestamp}-{random}
     * Used when converting stale references to new products
     */
    static generateTemporaryId(): string {
        return `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if an ID is a new cart item ID
     */
    static isNewCartItemId(id: string): boolean {
        return id.startsWith('new-');
    }

    /**
     * Convert existing cart item ID to new cart item ID (for stale references)
     * This is used when cart changes and we need to reset references
     */
    static convertToNewId(existingId: string): string {
        if (NewCartItemIdGenerator.isNewCartItemId(existingId)) {
            return existingId; // Already a new ID
        }
        // For stale references, use temporary ID format
        return NewCartItemIdGenerator.generateTemporaryId();
    }

    /**
     * Generate cart item ID from CartItemReference data
     * Handles both simple and configurable products
     *
     * @param productId - The product ID (child product for configurable)
     * @param parentProduct - Optional parent product for configurable products
     * @returns Generated cart item ID in appropriate format
     *
     * @example
     * // Simple product
     * generateFromReference(12345, undefined) // "new-12345"
     *
     * // Configurable product
     * generateFromReference(67890, parentProductModel) // "new-12345.67890"
     */
    static generateFromReference(productId: number | string, parentProduct?: ProductModel): string {
        if (parentProduct) {
            // Configurable product: use parent.id as itemId and product.id as productId
            return NewCartItemIdGenerator.generateForConfigurable(`${parentProduct.id ?? ''}`, `${productId}`);
        }
        // Simple product: use product.id as itemId
        return NewCartItemIdGenerator.generateFromItemId(productId);
    }

    /**
     * @deprecated Use generateFromItemId instead
     */
    static generateFromProductId(productId: number | string): string {
        return NewCartItemIdGenerator.generateFromItemId(productId);
    }
}
